<template>
  <v-container fluid class="customer-mini-page pa-0">
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="resetAndLoad"
    />

    <v-card
      :loading="loading && customers.length === 0"
      outlined
      class="customer-mini-card"
    >
      <div class="customer-mini-header">
        <div>
          <div class="page-title">Customers</div>
          <div class="page-subtitle">
            {{ customers.length
            }}{{ totalCount ? " / " + totalCount : "" }} loaded
          </div>
        </div>

        <div class="customer-mini-actions">
          <v-btn
            v-if="checkRightStatus(7)"
            small
            rounded
            color="primary"
            dark
            @click="add"
          >
            <v-icon left small>mdi-plus</v-icon>
            New
          </v-btn>

          <v-btn small rounded outlined to="/customer/list">
            <v-icon left small>mdi-format-list-bulleted</v-icon>
            List
          </v-btn>
        </div>
      </div>

      <div class="customer-search-bar">
        <v-text-field
          v-model="search"
          label="Search customer"
          outlined
          autofocus
          prepend-inner-icon="search"
          dense
          single-line
          hide-details
          clearable
          class="customer-search"
        />
      </div>

      <v-card-text
        ref="scrollArea"
        class="customer-scroll-area pa-0"
        @scroll.passive="handleScroll"
      >
        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          :items="customers"
          :loading="loadingMore"
          :sort-desc="sortDesc"
          :sort-by="sortBy"
          hide-default-footer
          :items-per-page="customers.length"
          class="customer-mini-table"
        >
          <template v-slot:item="{ item }">
            <tr class="pointer customer-row" @click="viewProfile(item)">
              <td class="text-left">
                <div class="customer-main">
                  <div class="customer-avatar">
                    {{ getInitial(item.name) }}
                  </div>

                  <div class="customer-info">
                    <div
                      class="customer-name"
                      v-html="
                        $highlight(item.name, (search || '').toLowerCase())
                      "
                    >
                      {{ item.name }}
                    </div>

                    <div
                      class="customer-phone"
                      v-html="
                        $highlight(
                          item.cellNumber,
                          (search || '').toLowerCase()
                        )
                      "
                    >
                      {{ item.cellNumber }}
                    </div>
                  </div>
                </div>
              </td>

              <td class="text-center">
                <v-tooltip bottom v-if="item.blockOrder">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="red" v-bind="attrs" v-on="on">
                      mdi-cancel
                    </v-icon>
                  </template>
                  <span>Ordering is blocked</span>
                </v-tooltip>
              </td>

              <td class="text-right">
                <span v-if="checkRightStatus(35)" class="customer-balance">
                  {{ item.balance | currency }}
                </span>
              </td>
            </tr>
          </template>

          <v-alert
            slot="no-results"
            :value="!loading && customers.length === 0"
            color="error"
            icon="warning"
          >
            Your search for "{{ search }}" found no results.
          </v-alert>
        </v-data-table>

        <div v-if="loadingMore" class="load-more-row">
          <v-progress-circular indeterminate size="24" />
          <span class="ml-3">Loading more...</span>
        </div>

        <!-- <div v-intersect="onIntersect" class="scroll-sentinel"></div> -->

        <div v-if="done && customers.length > 0" class="end-message">
          No more customers
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import customerService from "../service.js";

const Create = () => import("./Create");

export default {
  name: "customer-mini-list",

  components: {
    Create,
  },

  data() {
    return {
      createDialog: false,
      editDialog: false,
      selectedCustomer: null,

      headers: [
        {
          text: "Customer",
          value: "name",
          sortable: true,
          align: "left",
        },
        {
          text: "",
          value: "blockOrder",
          sortable: false,
          align: "center",
          width: "48px",
        },
        {
          text: this.$t("labels.balance"),
          value: "balance",
          width: "30%",
          align: "right",
          sortable: true,
        },
      ],

      sortBy: "balance",
      sortDesc: true,

      customers: [],
      totalCount: 0,
      limit: 60,
      offset: 0,
      loading: true,
      loadingMore: false,
      done: false,

      search: "",
      total: 0,
      fullTotal: 0,
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
  },

  watch: {
    search() {
      clearTimeout(this._searchTimer);

      this._searchTimer = setTimeout(() => {
        this.resetAndLoad();
      }, 300);
    },
  },

  created() {
    this.resetAndLoad();
    this.$events.listen("CustomerMiniList", () => this.resetAndLoad());
  },

  beforeDestroy() {
    this.$events.remove("CustomerMiniList");

    if (this._searchTimer) {
      clearTimeout(this._searchTimer);
    }
  },

  methods: {
    ...mapActions("customer", ["setCustomer"]),
    async handleScroll(e) {
      const el = e.target;

      const nearBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 120;

      if (!nearBottom || this.loading || this.loadingMore || this.done) {
        return;
      }

      await this.loadMore();
    },
    getInitial(name) {
      if (!name) {
        return "?";
      }

      return name.charAt(0).toUpperCase();
    },

    updateTotal(val) {
      this.total = val.reduce((sum, row) => {
        return sum + Number(row.balance || 0);
      }, 0);
    },

    viewProfile(customer) {
      this.setCustomer(customer.id);
    },

    add() {
      this.createDialog = true;
    },

    async resetAndLoad() {
      this.loading = true;
      this.loadingMore = false;
      this.done = false;
      this.offset = 0;
      this.customers = [];
      this.totalCount = 0;
      this.total = 0;
      this.fullTotal = 0;

      await this.loadMore();

      this.loading = false;
    },

    async loadMore() {
      if (this.loadingMore || this.done) {
        return;
      }

      this.loadingMore = true;

      try {
        const where = {
          limit: this.limit,
          offset: this.offset,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        };

        if (!this.currentUser.isAdmin && this.checkRightStatus(50)) {
          where.SalesRepUserId = this.currentUser.id;
        }

        if (this.search && this.search.trim().length > 0) {
          where.search = this.search.trim();
        }

        const res = await customerService.getAll(where);

        const count = Number(res.count || 0);
        const rows = Array.isArray(res.data) ? res.data : [];

        this.customers = this.customers.concat(rows);
        this.totalCount = count;

        rows.forEach((row) => {
          this.fullTotal += Number(row.balance || 0);
        });

        this.offset += rows.length;

        if (this.customers.length >= count || rows.length < this.limit) {
          this.done = true;
        }
      } catch (err) {
        console.error("Customer infinite load error:", err);
        this.done = true;
      } finally {
        this.loadingMore = false;
      }
    },

    editCustomer(customer) {
      this.selectedCustomer = customer;
      this.editDialog = true;
    },

    deleteCustomer(customer) {
      if (!this.checkRightStatus(39)) {
        this.$swal(
          "Delete Customer",
          "You are not allow to delete customer with balance.",
          "warning"
        );
        return;
      }

      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.value) {
          customerService
            .delete(customer.id)
            .then((response) => {
              if (response.data) {
                this.$swal("Deleted!", "Customer has been deleted.", "success");
                this.resetAndLoad();
              }
            })
            .catch((error) => {
              console.error("delete customer failed", error);
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.customer-mini-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

@media (max-width: 600px) {
  .customer-mini-header {
    flex-wrap: wrap;
  }

  .customer-mini-actions {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .customer-mini-actions .v-btn {
    width: 100%;
  }
}
.customer-mini-page {
  background: #f8fafc;
  height: 100%;
}

.customer-mini-card {
  border-radius: 20px;
  background: #ffffff;
  height: 100%;
  overflow: hidden;
}

.customer-mini-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.page-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.customer-search-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
}

.customer-search {
  width: 100%;
}

.customer-scroll-area {
  height: calc(100vh - 250px);
  width: 100%;
  overflow: auto;
}

.customer-mini-table {
  border-radius: 0;
}

.customer-row:hover {
  background: #f8fafc;
}

.customer-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.customer-avatar {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 12px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
}

.customer-info {
  min-width: 0;
}

.customer-name {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}

.customer-phone {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.customer-balance {
  font-weight: 800;
  color: #0f172a;
  white-space: nowrap;
}

.load-more-row {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #64748b;
  font-size: 13px;
}

.scroll-sentinel {
  height: 1px;
}

.end-message {
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  padding: 10px 0 14px;
}

@media (max-width: 600px) {
  .customer-scroll-area {
    height: calc(100vh - 250px);
  }
}
</style>